import { ITEMS } from '@/data/items';
import type { Recipe } from '@/data/recipes';
import type { Inventory } from '@/world/inventory';
import type { ToolBelt } from '@/world/toolbelt';
import { isTool } from '@/world/toolbelt';

/**
 * Turning materials into gear.
 *
 * Pure functions over the inventory and belt rather than a stateful Crafting
 * object: there is nothing to remember between crafts, and being stateless
 * means the UI can ask "could I make this?" as often as it likes.
 */

export type CraftResult =
  | 'crafted'
  | 'unknown-pattern'
  | 'missing-materials'
  | 'already-owned'
  | 'no-room'
  | 'not-yet';

export interface CraftContext {
  readonly inventory: Inventory;
  readonly belt: ToolBelt;
  /** Recipe ids taught by finished quests. */
  readonly unlocked?: ReadonlySet<string>;
}

/** Everything the recipe needs, and how much of it is to hand. */
export function ingredientStatus(
  recipe: Recipe,
  inventory: Inventory,
): { item: string; have: number; need: number; enough: boolean }[] {
  return recipe.inputs.map((input) => {
    const have = inventory.countOf(input.item);
    return { item: input.item, have, need: input.count, enough: have >= input.count };
  });
}

export function hasMaterials(recipe: Recipe, inventory: Inventory): boolean {
  return recipe.inputs.every((input) => inventory.countOf(input.item) >= input.count);
}

/** Why this recipe is unavailable, or null when it can be made right now. */
export function blockedReason(recipe: Recipe, context: CraftContext): CraftResult | null {
  const { inventory, belt } = context;

  // A pattern nobody taught you. Checked before anything else, so a locked
  // recipe never reports a reason that implies it is otherwise available.
  if (recipe.locked && !context.unlocked?.has(recipe.id)) return 'unknown-pattern';

  // Bag upgrades are a ladder: each needs the previous size, so they cannot be
  // bought out of order or twice.
  if (recipe.requiresCapacity !== undefined && inventory.capacity !== recipe.requiresCapacity) {
    return inventory.capacity > recipe.requiresCapacity ? 'already-owned' : 'not-yet';
  }

  if (recipe.output && isTool(recipe.output)) {
    // A tool you already carry, or already have a better version of.
    if (belt.acquireWouldDoNothing(recipe.output)) return 'already-owned';
  } else if (recipe.output && inventory.stackSizeOf(recipe.output) === 1) {
    if (inventory.countOf(recipe.output) > 0) return 'already-owned';
  }

  if (!hasMaterials(recipe, inventory)) return 'missing-materials';

  if (recipe.output && !isTool(recipe.output) && !inventory.hasRoomFor(recipe.output, recipe.outputCount)) {
    return 'no-room';
  }

  return null;
}

export function canCraft(recipe: Recipe, context: CraftContext): boolean {
  return blockedReason(recipe, context) === null;
}

/**
 * Make one.
 *
 * Materials come out only once the result is known to land somewhere, so a
 * full bag or a redundant upgrade costs the player nothing — losing a master
 * rod's worth of pearls to a capacity check would be unforgivable.
 */
export function craft(recipe: Recipe, context: CraftContext): CraftResult {
  const blocked = blockedReason(recipe, context);
  if (blocked) return blocked;

  const { inventory, belt } = context;
  for (const input of recipe.inputs) inventory.remove(input.item, input.count);

  if (recipe.bagSlots) inventory.expand(recipe.bagSlots);

  if (recipe.output) {
    // Tools go on the belt, where a better one retires the old.
    if (isTool(recipe.output)) belt.acquire(recipe.output);
    else inventory.add(recipe.output, recipe.outputCount);
  }

  return 'crafted';
}

export function describeCraftResult(result: CraftResult, name: string): string {
  switch (result) {
    case 'crafted':
      return `Made a ${name}`;
    case 'missing-materials':
      return 'You are missing materials';
    case 'already-owned':
      return `You already have that`;
    case 'no-room':
      return 'Your bag is full';
    case 'not-yet':
      return 'Not yet — upgrade the one before it first';
    case 'unknown-pattern':
      return 'You do not know that pattern';
  }
}

/** Display name for a recipe, whether it makes an item or grants an effect. */
export function craftName(recipe: Recipe): string {
  if (recipe.name) return recipe.name;
  return recipe.output ? ITEMS[recipe.output].name : recipe.id;
}
